import {invert} from 'lodash';

import {ActionWithPayload, batch} from '../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {DomainModel} from '../../../../models/api/domainModel';
import {AppAction} from '../../../appActions';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {
  createAccountAsyncAction,
  CreateAccountOptions,
  updateAccountAvatarAsyncAction,
} from '../../../data/accounts/accountsActions';
import {showAccountsErrorBannerAction, showAccountsInfoBannerAction} from '../../app/banners/bannersActions';
import {AccountsErrorBannerTypesEnum, AccountsInfoBannerTypesEnum} from '../../app/banners/bannersState';
import {closeSidePanelAction} from '../../contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {PanelActionPayload} from '../../panelsActions';
import {getPanels} from '../../panelsSelectors';
import {panelIsContactDetailsPanel, panelIsContactsManagerPanel} from '../../panelsState';
import {ContactDetailsPanelId, ContactsManagerPanelId} from '../../panelStateBase';

/*
 * Type enum.
 */

export enum CreateAccountFormActionsEnum {
  // Form state
  UPDATE_NAME = '@@panels/accounts/create-account-form/UPDATE_NAME',
  UPDATE_DESCRIPTION = '@@panels/accounts/create-account-form/UPDATE_DESCRIPTION',
  UPDATE_AVATAR_FILE = '@@panels/accounts/create-account-form/UPDATE_AVATAR_FILE',
  UPDATE_DOMAINS = '@@panels/accounts/create-account-form/UPDATE_DOMAINS',
  UPDATE_CUSTOM_ATTRIBUTE = '@@panels/accounts/create-account-form/UPDATE_CUSTOM_ATTRIBUTE',
  // Create account
  CREATE_ACCOUNT_STARTED = '@@panels/accounts/create-account-form/CREATE_ACCOUNT_STARTED',
  CREATE_ACCOUNT_COMPLETED = '@@panels/accounts/create-account-form/CREATE_ACCOUNT_COMPLETED',
  CREATE_ACCOUNT_FAILED = '@@panels/accounts/create-account-form/CREATE_ACCOUNT_FAILED',
}

export type ContactDetailsOrContactsManagerPanelId = ContactsManagerPanelId | ContactDetailsPanelId;

/*
 * Types.
 */

// Define all Payloads

type ContactDetailsOrContactsManagerPanelIdActionPayload =
  PanelActionPayload<ContactDetailsOrContactsManagerPanelId>;

interface UpdateNameActionPayload extends ContactDetailsOrContactsManagerPanelIdActionPayload {
  name: string;
}

interface UpdateDescriptionActionPayload extends ContactDetailsOrContactsManagerPanelIdActionPayload {
  description: string;
}

interface UpdateAvatarFileActionPayload extends ContactDetailsOrContactsManagerPanelIdActionPayload {
  avatarFile: File;
}

interface UpdateDomainsActionPayload extends ContactDetailsOrContactsManagerPanelIdActionPayload {
  domains: ReadonlyArray<DomainModel>;
}

interface UpdateCustomAttributeActionPayload extends ContactDetailsOrContactsManagerPanelIdActionPayload {
  customFieldId: number;
  value: string;
}

// Define all Action interfaces to include the appropriate Payload from above

interface UpdateNameAction extends ActionWithPayload<UpdateNameActionPayload> {
  type: CreateAccountFormActionsEnum.UPDATE_NAME;
}

interface UpdateDescriptionAction extends ActionWithPayload<UpdateDescriptionActionPayload> {
  type: CreateAccountFormActionsEnum.UPDATE_DESCRIPTION;
}

interface UpdateAvatarFileAction extends ActionWithPayload<UpdateAvatarFileActionPayload> {
  type: CreateAccountFormActionsEnum.UPDATE_AVATAR_FILE;
}

interface UpdateDomainsAction extends ActionWithPayload<UpdateDomainsActionPayload> {
  type: CreateAccountFormActionsEnum.UPDATE_DOMAINS;
}

interface UpdateCustomAttributeAction extends ActionWithPayload<UpdateCustomAttributeActionPayload> {
  type: CreateAccountFormActionsEnum.UPDATE_CUSTOM_ATTRIBUTE;
}

interface CreateAccountStartedAction
  extends ActionWithPayload<ContactDetailsOrContactsManagerPanelIdActionPayload> {
  type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_STARTED;
}

interface CreateAccountCompletedAction
  extends ActionWithPayload<ContactDetailsOrContactsManagerPanelIdActionPayload> {
  type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_COMPLETED;
}

interface CreateAccountFailedAction
  extends ActionWithPayload<ContactDetailsOrContactsManagerPanelIdActionPayload> {
  type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_FAILED;
}

/*
 * Action creators.
 */

function updateNameAction(panelId: ContactDetailsOrContactsManagerPanelId, name: string): UpdateNameAction {
  return {
    type: CreateAccountFormActionsEnum.UPDATE_NAME,
    payload: {
      panelId,
      name,
    },
  };
}

function updateDescriptionAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  description: string,
): UpdateDescriptionAction {
  return {
    type: CreateAccountFormActionsEnum.UPDATE_DESCRIPTION,
    payload: {
      panelId,
      description,
    },
  };
}

function updateAvatarFileAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  avatarFile: File,
): UpdateAvatarFileAction {
  return {
    type: CreateAccountFormActionsEnum.UPDATE_AVATAR_FILE,
    payload: {
      panelId,
      avatarFile,
    },
  };
}

function updateDomainsAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  domains: ReadonlyArray<DomainModel>,
): UpdateDomainsAction {
  return {
    type: CreateAccountFormActionsEnum.UPDATE_DOMAINS,
    payload: {
      panelId,
      domains,
    },
  };
}

function updateCustomAttributeAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  customFieldId: number,
  value: string,
): UpdateCustomAttributeAction {
  return {
    type: CreateAccountFormActionsEnum.UPDATE_CUSTOM_ATTRIBUTE,
    payload: {
      panelId,
      customFieldId,
      value,
    },
  };
}

function createAccountStartedAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
): CreateAccountStartedAction {
  return {
    type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_STARTED,
    payload: {
      panelId,
    },
  };
}

function createAccountCompletedAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
): CreateAccountCompletedAction {
  return {
    type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_COMPLETED,
    payload: {
      panelId,
    },
  };
}

function createAccountFailedAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
): CreateAccountFailedAction {
  return {
    type: CreateAccountFormActionsEnum.CREATE_ACCOUNT_FAILED,
    payload: {
      panelId,
    },
  };
}

/*
 * Union type.
 */

export type CreateAccountFormAction =
  | UpdateNameAction
  | UpdateDescriptionAction
  | UpdateAvatarFileAction
  | UpdateDomainsAction
  | UpdateCustomAttributeAction
  | CreateAccountStartedAction
  | CreateAccountCompletedAction
  | CreateAccountFailedAction;

/*
 * Actions.
 */

export function createAccountFormUpdateNameAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  name: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return;
    }

    dispatch(updateNameAction(panel.id, name));
  };
}

export function createAccountFormUpdateDescriptionAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  description: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return;
    }

    dispatch(updateDescriptionAction(panel.id, description));
  };
}

export function createAccountFormUpdateAvatarFileAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  avatarFile: File,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return;
    }

    dispatch(updateAvatarFileAction(panel.id, avatarFile));
  };
}

export function createAccountFormUpdateDomainsAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  domains: ReadonlyArray<DomainModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return;
    }

    dispatch(updateDomainsAction(panel.id, domains));
  };
}

export function createAccountFormUpdateCustomAttributeAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  customFieldId: number,
  value: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return;
    }

    dispatch(updateCustomAttributeAction(panel.id, customFieldId, value));
  };
}

export function createAccountFormCreateAccountAction(
  panelId: ContactDetailsOrContactsManagerPanelId,
  createOptions: CreateAccountOptions,
  avatarFile: File | undefined,
): ThunkAsyncAction<AccountModel | undefined> {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || (!panelIsContactDetailsPanel(panel) && !panelIsContactsManagerPanel(panel))) {
      return undefined;
    }

    try {
      dispatch(createAccountStartedAction(panelId));

      const newAccount = await dispatch(createAccountAsyncAction(createOptions));

      // Setting the avatar is a separate endpoint so we make a subsequent request
      if (avatarFile) {
        await dispatch(updateAccountAvatarAsyncAction(newAccount, avatarFile));
      }

      batch(() => {
        dispatch(createAccountCompletedAction(panelId));
        dispatch(showAccountsInfoBannerAction(panelId, AccountsInfoBannerTypesEnum.ACCOUNT_CREATED));
        dispatch(closeSidePanelAction());
      });

      return newAccount;
    } catch (error) {
      batch(() => {
        dispatch(showAccountsErrorBannerAction(panelId, AccountsErrorBannerTypesEnum.CREATION_FAILED));
        dispatch(createAccountFailedAction(panelId));
      });
      return undefined;
    }
  };
}

/*
 * Type guards.
 */

const createAccountFormActionsEnumMap = invert(CreateAccountFormActionsEnum);
export function actionIsCreateAccountFormAction(action: AppAction): action is CreateAccountFormAction {
  return action.type in createAccountFormActionsEnumMap;
}
